const { getServerApiUrl } = require("./getServerUrl");
const { perfionPictureSizes } = require("../types/images.types");

const BASE_PERFION_IMAGE_URL = "https://perfionapipro.welda.be:4432/Perfion/";

const normalizePerfionImageId = (value) => {
  if (value == null) return null;
  const id = String(value).trim();
  return id.length > 0 ? id : null;
};

const getPerfionProductImageIds = (perfion_data) => {
  if (!perfion_data || typeof perfion_data !== "object") return [];

  const mainImageId = normalizePerfionImageId(
    perfion_data?.Images?.ProductMainImage?.value
  );
  const extraImageIds = perfion_data?.Images?.ExtraImages?.value;

  const ids = [];
  if (mainImageId) {
    ids.push(mainImageId);
  }
  // MMS may return ExtraImages as a single id string or an array of ids.
  const extraList = Array.isArray(extraImageIds)
    ? extraImageIds
    : extraImageIds != null
      ? [extraImageIds]
      : [];
  for (const extraId of extraList) {
    const normalized = normalizePerfionImageId(extraId);
    if (normalized) {
      ids.push(normalized);
    }
  }
  return ids;
};

const getPerfionBrandLogoId = (perfion_data) => {
  if (!perfion_data || typeof perfion_data !== "object") return null;

  const imageId = perfion_data?.Images?.RemBrandLogoOnProduct?.value;
  return typeof imageId === "string" && imageId.length > 0 ? imageId : null;
};

const getPerfionBrandLogoUrl = ({ perfion_data, dossier, sizeParam = null } = {}) => {
  const imageId = getPerfionBrandLogoId(perfion_data);
  if (!imageId) return "";

  return buildPerfionImageUrlFromId({
    imageId,
    sizeParam,
    dossier,
    isProductImage: false,
  });
};

const buildPerfionImageUrlFromId = ({
  imageId,
  sizeParam = null,
  dossier,
  isProductImage = false,
}) => {
  const normalizedId = normalizePerfionImageId(imageId);
  if (!normalizedId) return "";

  let url = `${getServerApiUrl()}/perfion/getImage/image.aspx?id=${normalizedId}`;

  if (sizeParam) {
    url += sizeParam.startsWith("&") ? sizeParam : `&size=${sizeParam}`;
  }

  if (isProductImage) {
    url += "&product=1";
    if (dossier?.number != null) {
      url += "&dossier=" + dossier.number;
    }
  }

  return url;
};

const transformPerfionImgURLToPortalImgURL = ({
  imageURL,
  sizeParam = null,
  dossier,
  isProductImage = false,
}) => {
  if (!imageURL) return "";

  // Single getImage route; backend watermark middleware uses query param "product" and dossier for watermark decision
  let url = imageURL.replace(
    BASE_PERFION_IMAGE_URL,
    getServerApiUrl() + "/perfion/getImage/"
  );

  if (sizeParam) {
    url = url + sizeParam;
  }

  // Product images: add product=1 so backend can apply dossier watermark when applicable
  if (isProductImage) {
    url += (url.includes("?") ? "&" : "?") + "product=1";
  }

  // <img src> requests don't send x-dossier or session cookie; add dossier to URL so backend can resolve req.dossier
  if (isProductImage && dossier?.number != null) {
    url += (url.includes("?") ? "&" : "?") + "dossier=" + dossier.number;
  }

  return url;
};

const getProductImageData = (product, pictureSize, dossier, options = {}) => {
  const isProductImage = !options.withoutWatermark;
  try {
    if (!product || typeof product !== "object") return [];

    const { Naam, perfion_data, ArtikelFotos } = product;
    const perfionImageIds = getPerfionProductImageIds(perfion_data);
    const hasPerfionImages = perfionImageIds.length > 0;
    const cacImages = Array.isArray(ArtikelFotos)
      ? ArtikelFotos.filter((image) => image?.media_type === "IMG" && image?.media_url)
      : [];
    const hasCacImages = cacImages.length > 0;

    const sizeParam =
      pictureSize === perfionPictureSizes.ORIGINAL ? "" : `&size=${pictureSize}`;

    if (hasPerfionImages) {
      const imageUrls = perfionImageIds
        .map((imageId) =>
          buildPerfionImageUrlFromId({
            imageId,
            sizeParam,
            dossier,
            isProductImage,
          })
        )
        .filter(Boolean);

      return imageUrls.map((imageUrl) => ({
        imageUrl,
        name: Naam || "",
      }));
    }

    if (hasCacImages) {
      return cacImages.map((image) => ({
        imageUrl: getServerApiUrl() + "/CAC/getImage/" + image.media_url,
        name: image.media_titel || Naam || "",
      }));
    }

    return [];
  } catch (error) {
    return [];
  }
};

module.exports = {
  getProductImageData,
  getPerfionProductImageIds,
  getPerfionBrandLogoId,
  getPerfionBrandLogoUrl,
  buildPerfionImageUrlFromId,
  transformPerfionImgURLToPortalImgURL,
};
